import { THEME } from '../constants/app.constant';

/**
 * useThemeLocalService hook provides localStorage-based theme management.
 * Stores and retrieves user's theme preference (light/dark).
 * 
 * @hook
 * @returns {Object} Theme local service methods
 * @returns {Function} returns.getTheme - Get theme from storage
 * @returns {Function} returns.setTheme - Save theme to storage
 * 
 * @example
 * ```tsx
 * const { getTheme, setTheme } = useThemeLocalService();
 * 
 * const currentTheme = getTheme();
 * setTheme(THEME.DARK);
 * ```
 */
const useThemeLocalService = () => {
  const THEME_KEY = 'theme';

  /**
   * Gets the current theme from localStorage.
   * 
   * @returns {string | null} Theme value or null if not set
   */
  const getTheme = () => {
    return localStorage.getItem(THEME_KEY);
  };

  /**
   * Saves the theme preference to localStorage.
   * 
   * @param {THEME} theme - Theme value to save (THEME.LIGHT or THEME.DARK)
   */
  const setTheme = (theme: THEME) => {
    localStorage.setItem(THEME_KEY, theme);
  };

  return {
    getTheme,
    setTheme
  };
};

export default useThemeLocalService;